import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../button';
import DropDownFields from '../drop-down/DropdownFieldExpanded';
import PrimaryButton from '../button/PrimaryButton';
import dayjs, { Dayjs } from 'dayjs';
import MultipleLineTextField from '../text-field/Multiple';
import TextFieldExpanded from '../text-field/TextFieldExpanded';
import DatePickerExpanded from '../date-picker/DatePickerExpanded';
import {
	DataPointTestKitMeasurement,
	ExtractedMeasurement,
	TestKitMeasurementResultVO,
	TestKitMeasurementResultValueVO,
} from 'utils/models/test-kit-measurement';
import { CircularProgress } from '@mui/material';
import { DataRow } from '../../utils/models/data-row';
import { IReduxState } from 'utils/models/redux-states';
import ApiService from 'utils/services/ApiService';
import Tooltip from '@mui/material/Tooltip';
import DeleteConfirmationDialog from '../dialog/Delete';
import {
	getAllFailedRequests,
	getTestResults,
	saveDeletionSyncCache,
	saveFailedRequest,
	saveSyncCache,
	saveTestResults,
	updateFailedRequest,
} from '../../utils/services/idbService';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { RiskScoreVO } from 'utils/models/risk-score';
import { formatNumber } from 'utils/functions/formatNumber';
import { evaluateFormula } from 'utils/functions/evaluateFormula';
import {
	setAllSitesData,
	updateSingleSitesDataPoints,
	deleteSingleSiteDatapoints,
} from 'utils/redux/reducers/allSitesLoadedReducer';
import { getAlarmsFromCache } from 'utils/functions/getAlarmsFromCache';
import { SiteAlarmSetting } from 'utils/models/location-alarms';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { useConnection } from '../../utils/helper/ConnectionContext';

// #fields to show per column(inputs and results)
const COLUMN_THRESHOLD = 6;

interface ExtendedRowProps {
	additionalStyle?: string;
	setSelectedRow?: (e: any) => void;
	inputData: ExtractedMeasurement[];
	outputData: ExtractedMeasurement[];
	timeStamp?: number;
	note?: string;
	userId?: string;
	row: DataRow;
	originalData?: DataPointTestKitMeasurement;
	isOwner?: boolean;
	siteId: number;
	onSaveSuccess?: (updatedRow?: any) => void;
}

interface TestKitOutputDescriptor {
	id?: number;
	outputName: string;
	outputId: string;
	unit: string;
	maxVal: number;
	minVal: number;
	toPrev: boolean;
	formula: string;
	hide: boolean;
	hiHiValue?: number;
	hiValue?: number;
	loValue?: number;
	loLoValue?: number;
	outPutType?: string;
	// ... add other fields as necessary
}

const ExtendedRow: React.FC<ExtendedRowProps> = ({
	additionalStyle = '',
	inputData,
	outputData,
	timeStamp,
	setSelectedRow,
	note,
	userId,
	onSaveSuccess,
	row,
	originalData,
	isOwner,
	siteId,
}) => {
	const sendPopUpMessage = usePopupMessage();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const [selectedLocation, setSelectedLocation] = useState<string>(
		row?.allLocations
	);
	const [testKitsUnits, setTestKitsUnits] = useState<{ [key: string]: any }>(
		[]
	);
	const [selectedTestKit, setSelectedTestKit] = useState<string>(
		row?.allTests
	);
	const locationOptions = ['Slop Tank - Starboard Side', row?.allLocations];
	const testkitsOptions = ['Fluid', row?.allTests];
	const [descriptionValue, setDescriptionValue] = useState<string>(
		note ?? ''
	);
	const initialDate = timeStamp ? dayjs(timeStamp) : dayjs();
	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(initialDate);
	const [isEditing, setIsEditing] = useState(false);
	const [loading, setLoading] = useState(false);
	const [inputDataValues, setInputDataValues] = useState<string[]>(
		inputData.map((item) => item.value.toString())
	);
	const [outputDataValues, setOutputDataValues] = useState<string[]>(
		outputData.map((item) => item.value.toString())
	);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [outputValues, setOutputValues] = useState<Record<string, number>>(
		outputData.reduce((accumulator, currentValue) => {
			const value = parseFloat(currentValue.value);
			if (!isNaN(value)) {
				// Make sure the parsed value is a number
				accumulator[currentValue.name] = value;
			}
			return accumulator;
		}, {} as Record<string, number>)
	);

	const [testKitOutputDescriptorVOs, setTestKitOutputDescriptorVOs] =
		useState<TestKitOutputDescriptor[]>([]);
	const [riskScoreVO, setRiskScoreVO] = useState<RiskScoreVO>();
	const dispatch = useDispatch();
	const openDeleteDialog = () => {
		setIsDeleteDialogOpen(true);
	};
	const [alarmsFromCache, setAlarmsFromCache] = useState<SiteAlarmSetting[]>(
		[]
	);
	const { isConnected, setIsConnected } = useConnection();

	const testKitId = originalData?.testKitMeasurementResultVOs[0]?.testKitId;

	useEffect(() => {
		const testKitCache = new Map<string, { descriptors: any; riskScoreVO: any }>();
		const fetchTestKitOutputDescriptorVOs = async () => {
			const username = localStorage.getItem('currentUser') ?? 'unknown';

			// Run the two independent reads in parallel instead of sequentially
			const [alarms, catchunits] = await Promise.all([
				getAlarmsFromCache(siteId),
				getTestResults('allDataPointsUnits', username),
			]);
			setAlarmsFromCache(alarms);
			if (catchunits) setTestKitsUnits(catchunits[0]);

			if (!testKitId) return;

			// Return immediately if this test kit was already fetched this session
			const cacheKey = `${testKitId}_${originalData?.samplePointId}`;
			if (testKitCache.has(cacheKey)) {
				const cached = testKitCache.get(cacheKey)!;
				if (cached.descriptors) setTestKitOutputDescriptorVOs(cached.descriptors);
				if (cached.riskScoreVO) setRiskScoreVO(cached.riskScoreVO);
				return;
			}

			// Only show the spinner for the slow network/IDB call
			setLoading(true);
			try {
				let testKitData;
				if (navigator.onLine) {
					const response = await ApiService.getTestKitByIDAndSamplePointId(testKitId, originalData?.samplePointId);
					testKitData = response.data;
				} else {
					const testKits = await getTestResults('defaultTestKits', username);
					testKitData = testKits.find((kit: { id: number }) => kit.id === testKitId);
					if (!testKitData) throw new Error('No offline data available for this test kit');
				}
				const descriptors = testKitData?.testKitOutputDescriptorVOs || testKitData?.fieldDescriptorAllVOs;

				// Store in module cache so subsequent expands are instant
				testKitCache.set(cacheKey, {
					descriptors: descriptors ?? null,
					riskScoreVO: testKitData?.riskScoreVO ?? null,
				});

				if (descriptors) setTestKitOutputDescriptorVOs(descriptors);
				if (testKitData?.riskScoreVO) setRiskScoreVO(testKitData.riskScoreVO);
			} catch (error) {
				console.error('Error fetching test kit output descriptors:', error);
			} finally {
				setLoading(false);
			}
		};
		fetchTestKitOutputDescriptorVOs();
	}, []);

	const closeDeleteDialog = () => {
		setIsDeleteDialogOpen(false);

	}
	const unitLabel = (name: string) => {
		const unit = testKitsUnits?.[testKitId as number]?.[name]?.unit;
		return unit ? ` (${unit})` : '';
	};

	// Temporary display safeguard for percentage outputs. The stored/calculated
	// value is left unchanged; this only prevents the expanded view from showing
	// an invalid percentage while descriptor data is loading.
	const clampPercentageDisplayValue = (name: string, value: number) => {
		const unit = testKitsUnits?.[testKitId as number]?.[name]?.unit;
		const baseName = name.replace(/\s*\([^)]*\)$/, '').trim();
		const isPercentage = unit === '%' || ['ABR', 'BSI'].includes(baseName);
		return isPercentage ? Math.min(100, Math.max(0, value)) : value;
	};

	//Evaluates both formulae, and then checks the values against the current risk score weights
	function evaluateRiskScore(
		newInputValues: Record<string, number>,
		currentOutputValues: Record<string, number>
	): number {
		if (riskScoreVO) {
			let formulaResultX = evaluateFormula(
				riskScoreVO.formulaXAxis,
				newInputValues,
				currentOutputValues
			);
			let formulaResultY = evaluateFormula(
				riskScoreVO.formulaYAxis,
				newInputValues,
				currentOutputValues
			);

			if (Number.isNaN(formulaResultX) || Number.isNaN(formulaResultY)) {
				return NaN;
			}

			let weightX = 0;
			let weightY = 0;

			if (formulaResultX < riskScoreVO.lowXaxisValue) {
				weightX = riskScoreVO.lowXaxisWeight;
			} else if (formulaResultX < riskScoreVO.highXaxisValue) {
				weightX = riskScoreVO.midXaxisWeight;
			} else {
				weightX = riskScoreVO.highXaxisWeight;
			}

			if (formulaResultY < riskScoreVO.lowYaxisValue) {
				weightY = riskScoreVO.lowYaxisWeight;
			} else if (formulaResultY < riskScoreVO.highYaxisValue) {
				weightY = riskScoreVO.midYaxisWeight;
			} else {
				weightY = riskScoreVO.highYaxisWeight;
			}

			return weightX + weightY;
		}
		return NaN;
	}

	const updateOutputValuesBasedOnInput = (
		newInputValues: Record<string, number>,
		currentOutputValues: Record<string, number>
	) => {
		const newOutputValues: Record<string, number> = {};
		testKitOutputDescriptorVOs.forEach(
			(descriptor: TestKitOutputDescriptor) => {
				try {
					if (descriptor.outputId === 'RiskScore') {
						newOutputValues[descriptor.outputId] =
							evaluateRiskScore(
								newInputValues,
								currentOutputValues
							);
					} else {
						// Updated the formula evaluation to include currentOutputValues

						let logRoundDown = false;
						if (descriptor.outputName.includes("Log #")) {
							logRoundDown = true;
						}

						newOutputValues[descriptor.outputId] = evaluateFormula(
							descriptor.formula,
							newInputValues,
							currentOutputValues,
							logRoundDown,
							undefined,
							undefined,
							descriptor.minVal,
							descriptor.maxVal
						);
					}
				} catch (error) {}
		}
	);

		if (!testKitOutputDescriptorVOs.length) {
			Object.keys(currentOutputValues).forEach((key) => {
				const testKitId = 
					originalData?.testKitMeasurementResultVOs[0]?.testKitId;
				const unit = testKitsUnits?.[testKitId as number]?.[key];
				if (!unit) return;
				if (unit.outputId === 'RiskScore') {
					newOutputValues[key] = evaluateRiskScore(newInputValues, currentOutputValues);
				} else if (unit.formula) {
					let logRoundDown = unit?.outputName?.includes('Log #') ?? false;
					newOutputValues[key] = evaluateFormula(
						unit.formula,
						newInputValues,
						currentOutputValues,
						logRoundDown,
						undefined,
						undefined,
						unit.minVal,
						unit.maxVal
					);
				}
			});
		}

		setOutputValues(newOutputValues);
	};

	const handleDelete = async () => {
		try {
			if (!originalData) {
				console.error('originalData is undefined');
				return;
			}

			setLoading(true);

			const updatedTestKitMeasurementResultVOs: TestKitMeasurementResultVO[] =
				originalData.testKitMeasurementResultVOs.map((item) => ({
					...item,
					status: 'Deleted',
				}));

			removeValueStatuses(updatedTestKitMeasurementResultVOs);

			const updatedData: any = {
				...originalData,
				status: 'Deleted',
				testKitMeasurementResultVOs: updatedTestKitMeasurementResultVOs,
				photonMasterId: isConnected ? localStorage.getItem('analyzerSerialNumber') ?? undefined : undefined,
			};
			delete updatedData?.timestamp;
			delete updatedData?.username;

			if (localStorage.getItem('localStorage') !== 'true' && navigator.onLine) {
				await ApiService.postDataPointResult(updatedData);
			}

			const deleteUserTestData = async (keyBase: string) => {
				const user =
					localStorage.getItem('currentUser') ?? 'unknown_user';
				let existingData = await getTestResults(keyBase, user);
				if (Array.isArray(existingData)) {
					const updatedDataSamplePointId = siteId;
					const updatedDataId = updatedData.id;

					let filteredData = existingData.map((item) => {
						if (item.id === updatedDataSamplePointId) {
							if (Array.isArray(item.sampleLocationAllVOs)) {
								const updatedSampleLocationAllVOs =
									item.sampleLocationAllVOs.map(
										(sampleLocation: any) => {
											if (
												sampleLocation.id ===
												updatedData.samplePointId
											) {
												const filteredDataPoints =
													sampleLocation.dataPointAllVOs.filter(
														(dataPoint: any) =>
															dataPoint.id !==
															updatedDataId
													);
												return {
													...sampleLocation,
													dataPointAllVOs:
														filteredDataPoints,
												};
											}
											return sampleLocation;
										}
									);

								return {
									...item,
									sampleLocationAllVOs:
										updatedSampleLocationAllVOs,
								};
							}
						}
						return item;
					});

					await saveTestResults(keyBase, filteredData, user);
				}
			};

			await deleteUserTestData('allSites');
			dispatch(
				deleteSingleSiteDatapoints({
					siteId: siteId,
					samplePointId: originalData.samplePointId,
					dataPointId: originalData.id,
				})
			);
			setSelectedRow?.(1000);
			sendPopUpMessage('Success', `Deleted successfully!`);
			addEventLog('Delete Test Success', 'Tests', siteId, undefined, `Test Id: ${originalData?.id}, User: ${localStorage.getItem('currentUser')}`);
			closeDeleteDialog();
			if(!navigator.onLine) {
				await saveDeletionSyncCache({
					deletedDataPoints: [originalData.id as number],
				}, "deletionSyncCache");
			}
			setTimeout(() => {
				setLoading(false);
			}, 3000);
		} catch (error) {
			console.error('Error deleting data:', error);
			addEventLog('Delete Test Failed', 'Tests', siteId, undefined, `Test Id: ${originalData?.id}, User: ${localStorage.getItem('currentUser')}, Error: ${error}`);
			setLoading(false);
		}
	};

	function removeValueStatuses(
		measurementResultVO: TestKitMeasurementResultVO[]
	) {
		measurementResultVO.forEach((resultVO) => {
			resultVO.testKitMeasurementResultValueVOs.forEach(
				(valueVO: any) => {
					delete valueVO?.status;
				}
			);
		});
	}

	function updateAlarmInCache(resultVO: TestKitMeasurementResultVO, cachedData: any) {
		resultVO.testKitMeasurementResultValueVOs.forEach((v) => {
			if (v.type !== 'Output') return;
			const cached = cachedData.valueVOs.find((c: any) => c.outputId === v.name);
			if (cached) cached.outputAlarm = v.alarmType;
		});
		
	}
	const handleInputChange = (index: number, value: string) => {
		// Update the input values array with the new value
		const updatedInputValues = [...inputDataValues];
		updatedInputValues[index] = value;
		setInputDataValues(updatedInputValues);

		// Convert updated input values to the expected format for updateOutputValuesBasedOnInput
		// Assuming inputData and inputDataValues are aligned by index
		const newInputValues: Record<string, number> = {};
		inputData.forEach((input, idx) => {
			const inputValue = parseFloat(updatedInputValues[idx]);
			if (!isNaN(inputValue)) {
				newInputValues[input.name] = inputValue;
			}
		});

		// Call the function to update the output values based on the new input values
		// We use the existing outputValues state to maintain any current values that are not recalculated
		updateOutputValuesBasedOnInput(newInputValues, outputValues);
	};

	// Remove the handleOutputChange function if it's no longer needed.

	const handleOutputChange = (index: number, value: string) => {
		const updatedValues = [...outputDataValues];
		updatedValues[index] = value;
		setOutputDataValues(updatedValues);
	};

	const saveData = async () => {
		if (!originalData) {
			console.error('originalData is undefined');
			setLoading(false);
			return;
		}
		setLoading(true);
		setIsSaving(true);

		const mapToTestKitMeasurementResultValueVO = (
			data: ExtractedMeasurement,
			index: number
		): TestKitMeasurementResultValueVO => {
			return {
				id: data.id ?? 0,
				name: data.name,
				value:
					data.type === 'Input'
						? inputDataValues[index]
						: outputDataValues[index],
				type: data.type ?? '',
				alarmType:
					data.type !== 'Input'
						? determineAlarmType(outputValues[data.name], data.name)
						: 10,
			};
		};

		const mappedInputData = inputData.map((data, index) =>
			mapToTestKitMeasurementResultValueVO(data, index)
		);
		const mappedOutputData = outputData.map((data, index) =>
			mapToTestKitMeasurementResultValueVO(data, index)
		);

		// Accessing the first item in the testKitMeasurementResultVOs array to get the properties
		const firstMeasurement = originalData.testKitMeasurementResultVOs[0];

		const testKitMeasurementResultVOs: TestKitMeasurementResultVO = {
			testKitId: firstMeasurement.testKitId, // Optional, so only included if exists in the firstMeasurement
			samplePointId: originalData.samplePointId,
			resultId: firstMeasurement.resultId ?? 0,
			timeStamp: selectedDate?.valueOf() ?? dayjs().valueOf(),
			status: originalData.status,
			testKitMeasurementResultValueVOs: [
				...mappedInputData,
				...mappedOutputData,
			],
		};

		const data: DataPointTestKitMeasurement = {
			id: originalData?.id ?? 0,
			timeStamp: selectedDate?.valueOf() ?? dayjs().valueOf(),
			samplePointName: originalData.samplePointName,
			samplePointId: originalData.samplePointId,
			status: originalData.status,
			addedBy: originalData.addedBy,
			note: descriptionValue,
			testKitMeasurementResultVOs: [testKitMeasurementResultVOs],
			supplementalDataValueVOs: originalData.supplementalDataValueVOs,
			photonMasterId: isConnected ? localStorage.getItem('analyzerSerialNumber') ?? undefined : undefined,
		};

		let localFlag = localStorage.getItem('localStorage') ?? 'false';
		let currentUser = localStorage.getItem('currentUser') ?? 'unknown';

		try {
			if (!originalData) {
				console.error('originalData is undefined');
				return;
			}

			//Cache information is needed here
			let allData = await getTestResults(
				`allSites`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);

			let site = allData.find(
				(site: { id: number }) => site.id === siteId
			);
			let location = site.sampleLocationAllVOs.find(
				(loc: { id: number }) => loc.id === data.samplePointId
			);
			let cachedDataPoint = location.dataPointAllVOs.find(
				(dp: { id: any }) => dp.id === data.id
			);

			let onlineAPIError = false;

			if (localFlag !== 'true') {
				await ApiService.postDataPointResult(data)
					.then((resp) => {
						onSaveSuccess?.({
							...resp.data,
						});
					})
					.catch(async (e) => {
						if (navigator.onLine) {
							onlineAPIError = true;
						}
						const formattedDate = dayjs(data.timeStamp).format(
							'YYYY/MM/DD - hh:mm A'
						);
						const newRow: any = {
							...row,
							date: formattedDate,
							timestamp: data.timeStamp,
							notes: data.note,
							outputs: {},
						};

						data?.testKitMeasurementResultVOs?.forEach(
							(resultVO: any) => {
								resultVO.testKitMeasurementResultValueVOs.forEach(
									(valueVO: any) => {
										newRow.outputs[valueVO.name] =
											valueVO.value; // Update only the matching outputs
										newRow.outputAlarm[valueVO.name] =
											valueVO.alarmType;
									}
								);
							}
						);
						dispatch(
							updateSingleSitesDataPoints({
								siteId,
								...newRow,
							})
						);
						await saveSyncCache(
							{
								id: data.id ?? new Date().getTime(),
								locationFK: data.samplePointId,
								operatorId: data.addedBy,
								testKitId: data.testKitMeasurementResultVOs[0].testKitId ?? 0,
								note: data.note ?? "",
								timestamp: data.timeStamp,
								resultId: data.testKitMeasurementResultVOs[0].resultId ?? 0,
								deviceSerial: data.photonMasterId ?? "",
								valueDatas:
									data.testKitMeasurementResultVOs[0].testKitMeasurementResultValueVOs.map(
										(valueVO) => ({
											id: valueVO.id,
											outputId: valueVO.name,
											outputAlarm: valueVO.alarmType ?? 10,
											outputValue: valueVO.value.toString() ?? "0",
											type: valueVO.type,
										})
									),
								action: "UPDATE",
							},
							"dataPointSyncCache"
						);
						setSelectedRow?.(1000);

						//This is where we need to update an existing offline create data point, if the ID is a newID
						if (Number.isNaN(cachedDataPoint.id) && cachedDataPoint.id.includes("newID")) {
							const allSyncReqs = await getAllFailedRequests();
							let pendingSync = allSyncReqs.find((req: any) => req.tempID === cachedDataPoint.id);
							delete data.id; //dont put the ID string in the backend request
							await updateFailedRequest({
								url:
									process.env.REACT_APP_BACKEND_URI +
									'/cwds/services/dataservice/createdatapointresult/',
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify(data),
								tempID: pendingSync.tempID,
							}, pendingSync.key);
						} else {
							// await saveFailedRequest({
							// 	url:
							// 		process.env.REACT_APP_BACKEND_URI +
							// 		'/cwds/services/dataservice/createdatapointresult/',
							// 	method: 'POST',
							// 	headers: { 'Content-Type': 'application/json' },
							// 	body: JSON.stringify(data),
							// });
						}
						

						console.log(e);
					});
			}

			data.testKitMeasurementResultVOs.forEach((resultVO) => {
				updateAlarmInCache(resultVO, cachedDataPoint);
				updateValuesInCache(resultVO, cachedDataPoint);
				cachedDataPoint.timestamp = data.timeStamp;
				cachedDataPoint.note = data.note;
			});

			await saveTestResults(
				`allSites`,
				allData,
				currentUser);
			dispatch(setAllSitesData(allData));
			if (navigator.onLine && !onlineAPIError) {
				sendPopUpMessage('Success', 'Test saved successfully');
			} else if (navigator.onLine && onlineAPIError) {
				sendPopUpMessage('Result saved locally', `Cloud sync is temporarily unavailable. This result will sync automatically when restored.`);
				addEventLog('Edit Test Failure', 'Tests', siteId, undefined, `User: ${currentUser}`);
			} else {
				sendPopUpMessage('Success', 'Changes saved offline!');
			}
			
			if (!onlineAPIError) {
				addEventLog('Edit Test Success', 'Tests', siteId, undefined, `User: ${currentUser}`);
			}
			
			setIsSaving(false);
		} catch (error) {
			if (
				'serviceWorker' in navigator &&
				'SyncManager' in window &&
				localFlag !== 'true'
			) {
				try {
					const sw = await navigator.serviceWorker.ready;
					await sw.sync.register('syncTestResults');
				} catch (err) {
					console.error('Failed to register sync:', err);
				}
			}
			if(!navigator.onLine) {
				await saveSyncCache(
					{
						id: data.id ?? new Date().getTime(),
						locationFK: data.samplePointId,
						operatorId: data.addedBy,
						testKitId: data.testKitMeasurementResultVOs[0].testKitId ?? 0,
						deviceSerial: data.photonMasterId ?? "",
						note: data.note ?? "",
						timestamp: data.timeStamp,
						resultId: data.testKitMeasurementResultVOs[0].resultId ?? 0,
						valueDatas:
						data.testKitMeasurementResultVOs[0].testKitMeasurementResultValueVOs.map(
							(valueVO) => ({
							id: valueVO.id,
							outputId: valueVO.name,
							outputAlarm: valueVO.alarmType ?? 10,
							outputValue: valueVO.value.toString() ?? "0",
							type: valueVO.type,
							})
						),
						action: "UPDATE",
					},
					"dataPointSyncCache"
				);
			}
			console.error('Error saving data:', error); // Handle the error as needed
			addEventLog('Edit Test Failed', 'Tests', siteId, undefined, `User: ${localStorage.getItem('currentUser')}`);
			setIsSaving(false); // Reset saving state
		}

		setLoading(false); // End loading
	};

	function updateValuesInCache(
		resultVO: TestKitMeasurementResultVO,
		cachedData: any
	) {
		resultVO.testKitMeasurementResultValueVOs.forEach((valueVO) => {
			let cachedValue = cachedData.valueVOs.find((cachedVal: any) =>
				cachedVal.outputId === valueVO.name);
			if (cachedValue)
				cachedValue.outputValue = valueVO.value;
		});
	}
	const handleEditClick = () => {
		setIsEditing(!isEditing);
	};
	const handleDateChange = (value: unknown) => {
		if (dayjs.isDayjs(value) || value === null) 
			setSelectedDate(value as Dayjs);
	};

	const handleDescriptionChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		setDescriptionValue(e.target.value);
	};
	useEffect(() => {
		setDescriptionValue(note ?? '');
		setOutputDataValues(() =>
			outputData.map((item) => item.value.toString())
		);
		setOutputValues(() =>
			outputData.reduce((accumulator, currentValue) => {
				const value = parseFloat(currentValue.value);
				if (!isNaN(value)) {
					// Make sure the parsed value is a number
					accumulator[currentValue.name] = value;
				}
				return accumulator;
			}, {} as Record<string, number>)
		);
		setInputDataValues(() =>
			inputData.map((item) => item.value.toString())
		);
	}, [note, outputData, inputData]);

	const allowUserToEdit = () => {
		if (!reduxState?.userInformation?.userPermissionVOs) return false;
		const userLevel = 
			reduxState?.userInformation.userPermissionVOs[0].level;
		return userLevel !== 3 || (userLevel === 3 && isOwner);
	};

	const [isOffline, setIsOffline] = useState(!navigator.onLine);

	useEffect(() => {
		// This function updates the state based on online/offline status.
		const updateOnlineStatus = () => {
			setIsOffline(!navigator.onLine);
		};

		// Add event listeners
		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		// Cleanup: remove event listeners on unmount to avoid memory leaks.
		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, [reduxState]);

	useEffect(() => {
		const updatedOutputDataValues = outputData.map((output) => {
			const value = outputValues[output.name];
			return value !== undefined ? value.toString() : '';
		});

		updatedOutputDataValues.forEach((value, index) => {
			handleOutputChange(index, value);
		});

		setOutputDataValues(updatedOutputDataValues);
	}, [outputValues]);

	const determineAlarmType = (value: number, fieldId: string): number => {
		let descriptor = alarmsFromCache.find(
			(alarm: SiteAlarmSetting) =>
				alarm.locationName === selectedLocation &&
				alarm.testKitId === testKitId &&
				alarm.fieldId === fieldId
		);

		if (!descriptor) return 10;

		const { hiAlarm, hiHiAlarm, loAlarm, loLoAlarm } = descriptor;

		if (
			typeof hiAlarm === 'undefined' &&
			typeof hiHiAlarm === 'undefined' &&
			typeof loAlarm === 'undefined' &&
			typeof loLoAlarm === 'undefined'
		) {
			return 10;
		}

		let isOneWayAlarm =
			typeof loAlarm === 'undefined' && typeof loLoAlarm === 'undefined';
		let isTwoWayAlarm =
			typeof loAlarm !== 'undefined' || typeof loLoAlarm !== 'undefined';

		if (isTwoWayAlarm) {
			if (typeof loLoAlarm !== 'undefined' && value <= loLoAlarm) {
				return 1;
			} else if (typeof loAlarm !== 'undefined' && value <= loAlarm) {
				return 2;
			}
		}

		if (isOneWayAlarm || isTwoWayAlarm) {
			if (typeof hiHiAlarm !== 'undefined' && value >= hiHiAlarm) {
				return 1;
			} else if (typeof hiAlarm !== 'undefined' && value >= hiAlarm) {
				return 2;
			}
		}

		return 3;
	};

	useEffect(() => {
		if (!inputDataValues || !isEditing) return;
		const newInputValues: Record<string, number> = {};
		inputData.forEach((input, idx) => {
			const inputValue = parseFloat(inputDataValues[idx]);
			if (!isNaN(inputValue)) 
				newInputValues[input.name] = inputValue;
		});
		updateOutputValuesBasedOnInput(newInputValues, outputValues);
	}, [outputValues]);


	const renderInputField = (item: ExtractedMeasurement, index: number, offset = 0) => (
		<TextFieldExpanded
			textFieldStyle='w-17'
			key={index}
			value={formatNumber(item.value.toString())}
			label={item.displayName ? item.displayName + unitLabel(item.name) : item.name}
			disabled={!isEditing}
			addStyle={isEditing ? '!ml-4 edit input' : 'input'}
			alarmType={item.alarmType}
			onChange={(e) => handleInputChange(index + offset, e.target.value)}
		/>
	);

	const renderOutputField = (item: ExtractedMeasurement, index: number) => (
		<TextFieldExpanded
			textFieldStyle='w-17'
			key={index}
			value={formatNumber(
				clampPercentageDisplayValue(item.name, outputValues[item.name] ?? 0).toString()
			)}
			label={item.displayName ? item.displayName + unitLabel(item.name) : item.name}
			disabled={true}
			isEditingOutput={isEditing}
			alarmType={determineAlarmType(outputValues[item.name], item.name)}
		/>
	);

	return (
		<div
			className={`flex justify-center mb-[16px] mt-2 ${additionalStyle} relative bg-[#E6F5FA]`}
			style={{ width: 'calc(100%)', paddingLeft: '16px', paddingRight: '8px' }}
		>
			<div className="min-h-[434px] bg-white w-full flex px-2 py-2 test-details">
				{/* Sample Section */}
				<div
					style={{
						width: '358px',
						flexShrink: 0,
					}}
					className=" py-2 text-left mb-2 sample"
				>
					<h3 className="text-[14px] ml-4 font-inter text-primary-grey font-bold mb-8 tracking-[0.17px] font-semibold">
						Sample
					</h3>
					<div className="flex">
						<TextFieldExpanded
							addStyle="truncate w-[250px] operator"
							value={userId ?? 'Unknown'}
							label="Operator"
							disabled
							showAvatar
						/>
					</div>
					<DatePickerExpanded
						label="Sampled"
						value={selectedDate}
						onChange={handleDateChange}
						disabled={!isEditing}
						addStyle={
							isEditing ? '!mb-8 !w-[320px] !ml-3 edit sampled' : '!mb-4 sampled'
						}
					/>
					<DropDownFields
						label={'Location'}
						value={selectedLocation}
						onChange={(v) => setSelectedLocation(v)}
						options={locationOptions}
						addStyle={
							isEditing
								? '!w-[338px] !mb-4 edit location'
								: '!w-[338px] mb-4 location'
						}
						addLabelStyle={
							isEditing ? '!w-[338px] !mb-4' : '!w-[338px] mb-4'
						}
						disabled={true}
					/>
					<DropDownFields
						label={'Test Kit'}
						value={selectedTestKit}
						onChange={(v) => setSelectedTestKit(v)}
						options={testkitsOptions}
						addStyle={
							isEditing
								? '!w-[202px] !mb-4 edit test-kit'
								: '!w-[202px] mb-4 test-kit'
						}
						addLabelStyle={
							isEditing ? '!w-[202px] !mb-4' : '!w-[202px] mb-4'
						}
						disabled={true}
					/>
					<MultipleLineTextField
						label="Notes"
						value={descriptionValue}
						onChange={handleDescriptionChange}
						onKeyDown={(e) => e.stopPropagation()}
						inputProps={{ onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() }}
						rows={4}
						addStyle={
							isEditing
								? '!mb-4 !w-[318px] !ml-3 edit notes'
								: '!mb-4 notes'
						}
						disabled={!isEditing}
					/>
				</div>

				<div
					style={{
						width: '8px',
						minHeight: '308px',
						top: '8px',
						left: '356px',
						borderRadius: '2px',
						background: '#F4F8F9',
					}}
				/>
				{/* Input Section */}
				<div className="flex w-fit">
					<div className="w-fit pl-4 pr-2 py-2 text-left mb-2 input">
						<h3 className="text-[14px] ml-4 font-inter text-primary-grey font-bold mb-8 tracking-[0.17px] font-semibold">
							Inputs
						</h3>
						{inputData.slice(0, COLUMN_THRESHOLD).map((item, i) =>
							renderInputField(item, i)
						)}
					</div>
					{/* Additional Input Section if more than 6 items */}
					{inputData.length > COLUMN_THRESHOLD ? (
						<div className="flex-1 pl-2 pr-2 py-2 text-left mb-2 mt-14">
							{inputData.slice(COLUMN_THRESHOLD).map((item, i) =>
								renderInputField(item, i, COLUMN_THRESHOLD)
							)}
						</div>
					) : (
						<div className="flex-1 pl-2 pr-2 py-2 text-left mb-2 mt-14 invisible pointer-events-none" aria-hidden="true">
							{inputData.slice(0, 1).map((item, i) =>
								renderInputField(item, i, COLUMN_THRESHOLD)
							)}
						</div>
					)}
				</div>
				{/* Results Section */}
				<div className="flex w-fit">
					<div className="flex-1 pl-2 pr-4 py-2 text-left mb-2 results">
						<h3 className="text-[14px] ml-4 font-inter text-primary-grey font-bold mb-8 tracking-[0.17px] font-semibold">
							Results
						</h3>
						{outputData.slice(0, COLUMN_THRESHOLD).map((item, i) =>
							renderOutputField(item, i)
						)}
					</div>

					{outputData.length > COLUMN_THRESHOLD && (
						<div className="flex-1 px-4 py-2 text-left mb-2 mt-14">
							{outputData.slice(COLUMN_THRESHOLD).map((item, i) =>
								renderOutputField(item, i)
							)}
						</div>
					)}
					{/* ... Additional Results-related components */}
					<div style={{ width: '80px', flexShrink: 0 }} />
					<div className="absolute bottom-[26px] right-4 flex space-x-2 items-center">
						{allowUserToEdit() && (
							<Button
								style={{
									maxWidth: '40px',
									maxHeight: '40px',
									minWidth: '40px',
									minHeight: '40px',
								}}
								addStyle="!h-[40px] w-[40px] !bg-tertiary-grey-2 !ml-4 delete"
								onClick={openDeleteDialog}
								disabled={loading}
							>
								<Tooltip title="Delete">
									<img
										src="/assets/icons/Trash.svg"
										alt="Drawer Icon"
										className="w-5 h-5 md:block hidden"
									/>
								</Tooltip>
							</Button>
						)}

						{/* Render the delete confirmation dialog */}
						<DeleteConfirmationDialog
							open={isDeleteDialogOpen}
							onClose={closeDeleteDialog}
							onConfirm={handleDelete}
						/>
						{isEditing ? (
							<>
								<PrimaryButton
									onClick={() => {
										if (!loading) {
											saveData();
											handleEditClick();
										}
									}}
									addStyle="!w-[138px] !h-[39px] !tracking-[0.46px] save-test"
									disabled={loading && isSaving}
								>
									Save Test
								</PrimaryButton>
								{loading && isSaving && (
									<CircularProgress
										size={22}
										color="primary"
										className="h-[10px] w-[10px] !ml-2 !mt-2"
									/>
								)}
							</>
						) : (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								{allowUserToEdit() && (
									<>
										{loading && isSaving ? (
											<>
												<PrimaryButton
													addStyle="!w-[138px] !h-[39px] !tracking-[0.46px] save-test"
													disabled={
														loading || isSaving
													}
												>
													Save Test
												</PrimaryButton>
												<CircularProgress
													size={22}
													color="primary"
													className="!ml-1"
												/>
											</>
										) : (
											<Button
												style={{
													maxWidth: '40px',
													maxHeight: '40px',
													minWidth: '40px',
													minHeight: '40px',
												}}
												addStyle="!h-[40px] w-[40px] !bg-tertiary-grey-2 edit"
												onClick={handleEditClick} // toggle to edit mode
											>
												<Tooltip title="Edit test">
													<img
														src={
															'/assets/icons/Edit.svg'
														}
														alt="Drawer Icon"
														className="w-5 h-5 md:block hidden"
													/>
												</Tooltip>
											</Button>
										)}
									</>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExtendedRow;